import { drawExtensionIcon } from '@/extensions/pixelIcons';
import { NO_NODE_HANDLE } from '@/models/logic/RuntimeHandles';
import { z } from 'zod';
import { configureTile } from '../configuration';
import { extensionCatalog, type DigitilesExtension } from '../catalog';
import { watchExtensionOutputs } from '../outputs';
import { InputType, type TileDefinition } from '@/models/tiles/TileDefinition';
import { CircuitType, Side } from '@/schemas/common';
import { TileSideIOType } from '@/schemas/tiles';
import { standardBuildAndConnect } from '@/models/logic/utils';
import {
  defaultTileGraphics,
  connectionGraphics,
} from '@/models/tiles/DefaultTileGraphics';
import { colors } from '@/styles';
export const SOUND_TYPE = 'extension:digitiles.sound/note' as const;
const configSchema = z.object({
  note: z.number().int().min(0).max(127),
  duration: z.number().min(50).max(2000),
  volume: z.number().min(0).max(1),
});
type Config = z.infer<typeof configSchema>;
const defaults = (): Config => ({ note: 69, duration: 250, volume: 0.2 });
const ioSides = {
  [Side.TOP]: { type: TileSideIOType.NONE, forceConnection: false },
  [Side.RIGHT]: { type: TileSideIOType.OUTPUT, forceConnection: true },
  [Side.BOTTOM]: { type: TileSideIOType.NONE, forceConnection: false },
  [Side.LEFT]: { type: TileSideIOType.INPUT, forceConnection: true },
};
const tile: TileDefinition<Config> = {
  type: SOUND_TYPE,
  name: 'Sound note',
  canRotate: true,
  stateSchema: configSchema,
  createDefaultState: defaults,
  graphConfiguration: (state) => ({
    note: state.note,
    duration: state.duration,
    volume: state.volume,
  }),
  ioSides,
  buildAndConnectNodes(data) {
    const queue = standardBuildAndConnect(SOUND_TYPE, data, tile);
    const node = data.graph.nodes.get(data.instance.logicNodeIds?.top ?? NO_NODE_HANDLE);
    if (node) Object.assign(node.state, data.instance.state);
    return queue;
  },
  createContextMenuItems: ({ instance, chipEditorModel }) =>
    (
      [
        ['note', 'MIDI note (0–127; 69 is A4)', 'MIDI note'],
        ['duration', 'Note duration in milliseconds (50–2000)', 'Duration'],
        ['volume', 'Note volume (0–1)', 'Volume'],
      ] as const
    ).map(([key, title, label]) => ({
      label:
        key === 'volume'
          ? `${label} (${Math.round(instance.state.volume * 100)}%)`
          : `${label} (${instance.state[key]})`,
      action: async (takeInput) => {
        try {
          const value = await takeInput(
            key === 'volume' ? InputType.SLIDER : InputType.NUMBER,
            title,
            instance.state[key],
            true,
            (input) => ({
              isValid: configSchema.shape[key].safeParse(Number(input)).success,
              errorMessage: title,
            }),
          );
          configureTile(
            instance,
            chipEditorModel,
            { [key]: configSchema.shape[key].parse(Number(value)) },
            `Change sound ${key}`,
          );
        } catch {
          /* Cancel keeps the configured note. */
        }
      },
    })),
  graphics: {
    type: SOUND_TYPE,
    draw(g, { meta, connections, context }) {
      defaultTileGraphics(g);
      connectionGraphics(
        g,
        colors.signalColor,
        colors.noSignalColor,
        meta,
        connections,
        ioSides,
        context,
      );
      drawExtensionIcon(g, 'sound', meta.outputs.right ? 0xe8c97b : 0x9a8760);
    },
  },
};
export const soundExtension: DigitilesExtension = {
  id: 'digitiles.sound',
  name: 'Sound notes',
  version: '1.0.0',
  description:
    'Play a configured musical note on each low-to-high input transition. Sound remains off until live output is enabled.',
  capabilities: ['audio'],
  tiles: [{ definition: tile, circuits: [CircuitType.ChipLogic] }],
  nodes: [
    {
      type: SOUND_TYPE,
      stateSchema: configSchema,
      configurationKeys: ['note', 'duration', 'volume'],
      createDefaultState: defaults,
      process: (input) => input,
    },
  ],
  activate(app, signal) {
    const Audio =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Audio) throw new Error('This browser does not support Web Audio.');
    const audio = new Audio();
    const voices = new Set<{ oscillator: OscillatorNode; gain: GainNode }>();
    const stopVoices = () => {
      for (const voice of voices) {
        try {
          voice.oscillator.stop();
        } catch {
          /* Already stopped. */
        }
        voice.oscillator.disconnect();
        voice.gain.disconnect();
      }
      voices.clear();
    };
    let closed = false;
    const closeAudio = () => {
      if (closed) return;
      closed = true;
      stopVoices();
      void audio.close().catch(() => {});
    };
    signal.addEventListener('abort', closeAudio, { once: true });
    const report = (error: unknown) => {
      if (!signal.aborted)
        extensionCatalog.setError(
          'digitiles.sound',
          `Audio unavailable: ${error instanceof Error ? error.message : String(error)}`,
        );
    };
    // A restored grant may activate during page load, before the browser allows
    // audio. Retry on the next user gesture without requiring a second grant.
    const resumeOnGesture = () => {
      if (!signal.aborted && audio.state === 'suspended')
        void audio.resume().catch(report);
    };
    window.addEventListener('pointerdown', resumeOnGesture, { signal });
    window.addEventListener('keydown', resumeOnGesture, { signal });
    void audio.resume().catch(report);
    const unwatch = watchExtensionOutputs(app, SOUND_TYPE, ({ node }, high) => {
      if (
        !high ||
        signal.aborted ||
        audio.state !== 'running' ||
        document.hidden ||
        voices.size >= 32
      )
        return;
      const state = node.state as Config;
      if (state.volume === 0) return;
      try {
        const oscillator = audio.createOscillator();
        const gain = audio.createGain();
        const voice = { oscillator, gain };
        voices.add(voice);
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(
          440 * 2 ** ((state.note - 69) / 12),
          audio.currentTime,
        );
        const start = audio.currentTime,
          end = start + state.duration / 1000;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(state.volume * 0.15, start + 0.005);
        gain.gain.exponentialRampToValueAtTime(0.0001, end);
        oscillator.connect(gain);
        gain.connect(audio.destination);
        oscillator.onended = () => {
          voices.delete(voice);
          oscillator.disconnect();
          gain.disconnect();
        };
        oscillator.start(start);
        oscillator.stop(end);
      } catch (error) {
        stopVoices();
        report(error);
      }
    });
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) stopVoices();
      },
      { signal },
    );
    return () => {
      unwatch();
      closeAudio();
    };
  },
};
